import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../firebase/AuthProvider';
import { lookupTask } from '../../../services/taskServices/TaskService';
import { getPublishedFaqs } from '../../../services/wikiServices/HelpService';
import ChatReferenceChip from './ChatReferenceChip';

export interface ChatLinkReference { kind: 'Task' | 'FAQ'; reference: string; href: string; key: string; faqMode?: 'id' | 'slug' }
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const friendlyTask = /^(?:([a-z0-9]+)-)?([0-9]+)$/i;
// Explicitly reject raw controls before URL normalization can discard them.
// eslint-disable-next-line no-control-regex
const unsafeHrefCharacters = /[\u0000-\u0020\u007f-\u009f\u061c\u200e\u200f\u202a-\u202e\u2066-\u2069\\]/;

/** Read references without changing the document, its labels, or destinations. */
export function taskFaqReferences(doc: unknown): ChatLinkReference[] {
  let root = doc;
  if (typeof root === 'string') { if (root.length > 262144) return []; try { root = JSON.parse(root); } catch { return []; } }
  const result = new Map<string, ChatLinkReference>(); const seen = new WeakSet<object>(); let visited = 0;
  const add = (href: unknown) => {
    if (typeof href !== 'string' || result.size >= 20 || href.length > 2048 || (!href.startsWith('/') && !/^https?:\/\//i.test(href)) || href.startsWith('//') ||
      unsafeHrefCharacters.test(href)) return;
    try {
      const url = new URL(href, window.location.origin);
      const origins = new Set([window.location.origin]);
      if (process.env.REACT_APP_CURRENT_URL) { try { origins.add(new URL(process.env.REACT_APP_CURRENT_URL).origin); } catch { /* Invalid optional origin grants no access. */ } }
      if (!origins.has(url.origin) || !/^https?:$/.test(url.protocol) || url.username || url.password) return;
      const task = url.pathname.match(/^\/task\/([^/]+)\/?$/i);
      let kind: 'Task' | 'FAQ'; let reference: string; let faqMode: 'id' | 'slug' | undefined;
      if (task) {
        reference = decodeURIComponent(task[1]); const match = reference.match(friendlyTask);
        if (!match || Number(match[2]) <= 0 || Number(match[2]) > 2147483647) return;
        kind = 'Task';
      } else {
        const article = url.pathname.match(/^\/faqs\/article\/([^/]+)\/?$/i);
        const slug = url.pathname.match(/^\/faqs\/([^/]+)\/?$/i);
        if (article) { reference = decodeURIComponent(article[1]); if (!uuid.test(reference)) return; reference = reference.toLowerCase(); faqMode = 'id'; }
        else if (slug) { reference = decodeURIComponent(slug[1]); if (!/^[a-z0-9][a-z0-9_-]{0,199}$/i.test(reference)) return; faqMode = 'slug'; }
        else return;
        kind = 'FAQ';
      }
      const key = `${kind}:${faqMode ?? 'friendly'}:${reference}`;
      if (!result.has(key)) result.set(key, { kind, reference, href, key, faqMode });
    } catch { /* Malformed/external references keep their original document rendering. */ }
  };
  const visit = (node: any, depth: number) => {
    if (!node || typeof node !== 'object' || depth > 32 || ++visited > 4000 || seen.has(node)) return;
    seen.add(node);
    if (node.type === 'mention' && node.attrs?.mentionSuggestionChar === '#' && typeof node.attrs?.id === 'string') add(`/task/${encodeURIComponent(node.attrs.id)}`);
    if (node.type === 'faqReference' && typeof node.attrs?.faqArticleId === 'string' && uuid.test(node.attrs.faqArticleId)) add(`/faqs/article/${node.attrs.faqArticleId}`);
    if (Array.isArray(node.marks)) for (const mark of node.marks) if (mark?.type === 'link') add(mark.attrs?.href);
    if (Array.isArray(node.content)) for (const child of node.content) visit(child, depth + 1);
  };
  visit(root, 0); return Array.from(result.values());
}

interface Preview { title: string; detail?: string; summary?: string }
export default function TaskFaqLinkPreviews({ doc, appearance }: { doc: unknown; appearance?: 'dark' }) {
  const { currentUser, currentUserInfo, currentOrganization, currentRoles, currentPolicies, currentOrganizationRolePolicies, impersonatedUserId } = useAuth();
  const refs = taskFaqReferences(doc); const refsKey = JSON.stringify(refs);
  const identity = JSON.stringify([currentUser?.uid, currentUserInfo?.id, currentUserInfo?.organization?.id,
    currentOrganization?.abbreviation, (currentRoles ?? []).map(role => role.name).sort(), impersonatedUserId]);
  const scope = useMemo(() => ({ identity, currentUser, currentUserInfo, currentOrganization, currentPolicies, currentOrganizationRolePolicies }),
    [identity, currentUser, currentUserInfo, currentOrganization, currentPolicies, currentOrganizationRolePolicies]);
  const [state, setState] = useState<{ scope: typeof scope; refsKey: string; rows: Record<string, Preview | null> } | null>(null);
  const refreshRef = useRef<(() => void) | null>(null);
  const rows = state?.scope === scope && state.refsKey === refsKey ? state.rows : {};
  useEffect(() => {
    let cancelled = false; let generation = 0; let lastRead = 0;
    const references: ChatLinkReference[] = JSON.parse(refsKey);
    const refresh = async () => {
      const request = ++generation;
      lastRead = Date.now();
      setState(previous => previous?.scope === scope && previous.refsKey === refsKey ? previous : { scope, refsKey, rows: {} });
      if (!currentUser?.uid || !currentUserInfo?.id || document.visibilityState === 'hidden') return;
      try {
        const token = await currentUser.getIdToken();
        if (cancelled || generation !== request) return;
        const faqIndex = references.some(ref => ref.kind === 'FAQ') ? getPublishedFaqs(token).catch(() => null) : Promise.resolve(null);
        const pairs = await Promise.all(references.map(async ref => {
          try {
            if (ref.kind === 'FAQ') {
              const categories = await faqIndex;
              const article = categories?.flatMap(category => category.articles ?? []).find(item =>
                ref.faqMode === 'id' ? item.id.toLowerCase() === ref.reference : item.slug === ref.reference);
              return [ref.key, article ? { title: article.title, summary: article.summary ? article.summary.slice(0, 350) + (article.summary.length > 350 ? '…' : '') : undefined } : null] as const;
            }
            const match = ref.reference.match(friendlyTask)!;
            const task = await lookupTask(String(Number(match[2])), token);
            const actorOrganizationId = currentUserInfo.organization?.id;
            if (!task || !actorOrganizationId || String(task.organization?.id) !== String(actorOrganizationId) || String(task.friendlyId) !== String(Number(match[2])) ||
              (match[1] && task.organization?.abbreviation?.toLowerCase() !== match[1].toLowerCase())) return [ref.key, null] as const;
            return [ref.key, { title: task.title, detail: task.status?.name }] as const;
          } catch { return [ref.key, null] as const; }
        }));
        if (!cancelled && generation === request) setState({ scope, refsKey, rows: Object.fromEntries(pairs) });
      } catch { if (!cancelled && generation === request) setState({ scope, refsKey, rows: Object.fromEntries(references.map(ref => [ref.key, null])) }); }
    };
    // Title lookup is bounded and user-driven; do not poll full task details
    // repeatedly across every rendered message in an idle chat.
    refreshRef.current = () => { if (Date.now() - lastRead >= 30000) void refresh(); };
    if (references.length) void refresh();
    const visible = () => void refresh();
    window.addEventListener('focus', visible); document.addEventListener('visibilitychange', visible);
    return () => { cancelled = true; ++generation; refreshRef.current = null; window.removeEventListener('focus', visible); document.removeEventListener('visibilitychange', visible); };
  }, [scope, currentUser, currentUserInfo, currentOrganization, refsKey]);
  if (!refs.length) return null;
  return <div className={`resource-reference-chips${appearance === 'dark' ? ' resource-reference-chips--dark' : ''}`} aria-label="Linked tasks and FAQs">
    {refs.map(ref => rows[ref.key] ? <ChatReferenceChip key={`${identity}:${ref.key}`} href={ref.href} title={rows[ref.key]!.title} kind={ref.kind} appearance={appearance} onPreview={() => refreshRef.current?.()}
      preview={<span className="resource-reference-tooltip-content"><strong>{rows[ref.key]!.title}</strong>
        {rows[ref.key]!.detail && <span>Status: {rows[ref.key]!.detail}</span>}
        {rows[ref.key]!.summary && <span>{rows[ref.key]!.summary}</span>}
        <span className="resource-reference-tooltip-action">Open {ref.kind.toLowerCase()} in Waivera</span></span>} />
      : <span key={ref.key} className="resource-reference-unavailable" role="status">{Object.prototype.hasOwnProperty.call(rows, ref.key) ? `${ref.kind} unavailable` : `Checking ${ref.kind.toLowerCase()} access…`}</span>)}
  </div>;
}
